/**
 * Which tab edits which field.
 *
 * Validation findings name a field, not a place. Without this the operator is told "Show type is not one
 * of the configured show types" and left to open each tab looking for it — which is the complaint: the
 * tool knows exactly where the problem is and does not say.
 *
 * Kept apart from the component and tested, because a wrong entry here is worse than no counter at all:
 * it sends someone to a tab that does not contain the field and offers no clue that the answer is
 * elsewhere.
 */

export type FieldTab =
  | 'general'
  | 'descriptions'
  | 'availability'
  | 'assets'
  | 'validation'
  | 'xml';

/**
 * Field name to tab. Mirrors what each tab actually renders, including the list fields that are not in
 * the `GENERAL`/`AVAILABILITY` definitions.
 */
const TAB_BY_FIELD: Record<string, FieldTab> = {
  // General
  title: 'general',
  titleBrief: 'general',
  titleSortName: 'general',
  showType: 'general',
  year: 'general',
  countryOfOrigin: 'general',
  runTime: 'general',
  displayRunTime: 'general',
  audience: 'general',
  audioType: 'general',
  hdContent: 'general',
  rating: 'general',
  genre: 'general',
  category: 'general',
  actors: 'general',
  director: 'general',
  languages: 'general',

  // Descriptions — also carries the series fields.
  summaryShort: 'descriptions',
  summaryMedium: 'descriptions',
  summaryLong: 'descriptions',
  episodeName: 'descriptions',
  seriesOrdinal: 'descriptions',
  episodeOrdinal: 'descriptions',
  episodeId: 'descriptions',

  // Availability
  maximumViewingLength: 'availability',
  suggestedPrice: 'availability',
  availableInLocalities: 'availability',
  restrictedDeviceTypes: 'availability',
  licensingWindowStart: 'availability',
  licensingWindowEnd: 'availability',
  grossPrice: 'availability',
  netPrice: 'availability',
};

/**
 * The tab that edits `field`, or `undefined` when nothing does.
 *
 * Asset rules report `assets.<type>` — `assets.poster`, `assets.box cover` — so the prefix is matched
 * rather than the whole string; there is no field named `assets.poster` to enumerate.
 *
 * Returning `undefined` rather than guessing at `general` matters: an unmapped field would otherwise be
 * counted on a tab that cannot fix it, and the operator would click through to nothing. The Validation
 * tab still lists it, which is the honest fallback.
 */
export const tabForField = (
  field: string | null | undefined,
): FieldTab | undefined => {
  if (!field) {
    return undefined;
  }
  if (field.startsWith('assets.') || field === 'assets') {
    return 'assets';
  }
  return TAB_BY_FIELD[field];
};

export interface TabCounts {
  errors: number;
  warnings: number;
}

/**
 * Findings per tab, so each tab can show what it is hiding.
 *
 * A finding whose field maps nowhere is left out of every tab's count rather than being attributed to
 * one arbitrarily — a count that cannot be acted on is noise. It remains visible on the Validation tab.
 */
export const countByTab = (
  findings: { field?: string | null; level?: string | null }[],
): Record<FieldTab, TabCounts> => {
  const counts: Record<FieldTab, TabCounts> = {
    general: { errors: 0, warnings: 0 },
    descriptions: { errors: 0, warnings: 0 },
    availability: { errors: 0, warnings: 0 },
    assets: { errors: 0, warnings: 0 },
    validation: { errors: 0, warnings: 0 },
    xml: { errors: 0, warnings: 0 },
  };

  for (const finding of findings) {
    const tab = tabForField(finding.field);
    if (!tab) {
      continue;
    }
    if (finding.level === 'ERROR') {
      counts[tab].errors++;
    } else {
      counts[tab].warnings++;
    }
  }

  // The Validation tab lists every finding, mapped or not, so its badge is the total.
  counts.validation = {
    errors: findings.filter((f) => f.level === 'ERROR').length,
    warnings: findings.filter((f) => f.level !== 'ERROR').length,
  };

  return counts;
};

/**
 * How many findings the header chip is entitled to claim.
 *
 * The chip takes its wording from the item's worst level, so the number beside it has to be the count
 * at *that* level. Handed the total instead, it read "4 errors" on an item with one error and three
 * warnings — overstating the blocking problems fourfold on the one control that says whether an item
 * can be exported.
 */
export const findingsAtWorstLevel = (
  worst: string | null | undefined,
  findings: { field?: string | null; level?: string | null }[],
): number =>
  worst === 'ERROR'
    ? findings.filter((f) => f.level === 'ERROR').length
    : findings.filter((f) => f.level !== 'ERROR').length;
